import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import useGooglePayChargeTransaction from '@/hooks/useApi/useGooglePayChargeTransaction';
import usePayPal from '@/hooks/useApi/usePayPal';
import useVenmo from '@/hooks/useApi/useVenmo';
import DigitalWalletTransactionInfo from './DigitalWalletTransactionInfo';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { TransactionId } from 'vopay';

type DigitalWalletTransactionContainerProps = TransactionId &
  Omit<ComponentProps<typeof DigitalWalletTransactionInfo>, 'transaction'>;

export default function DigitalWalletTransactionContainer({
  TransactionID,
  transactionType,
  fullName,
  jwt,
  ELinxRequestID = '',
  ...props
}: DigitalWalletTransactionContainerProps) {
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  // Sibling hooks return a generic `transaction`; alias each so the derived value below can pick one.
  const { transaction: googlePayChargeTransaction, fetchGooglePayChargeTransaction } = useGooglePayChargeTransaction({
    bearer: jwt,
  });

  const { transaction: payPalWithdrawTransaction, fetchPayPalWithdrawTransaction } = usePayPal({ bearer: jwt });
  const { transaction: venmoWithdrawTransaction, fetchVenmoWithdrawTransaction } = useVenmo({ bearer: jwt });

  const transaction =
    transactionType === 'GooglePay'
      ? googlePayChargeTransaction
      : transactionType === 'PayPal'
        ? payPalWithdrawTransaction
        : transactionType === 'Venmo'
          ? venmoWithdrawTransaction
          : undefined;

  useEffect(() => {
    blockingLoader.show();

    const fetchTransaction = async () => {
      try {
        if (transactionType === 'GooglePay') {
          await fetchGooglePayChargeTransaction(TransactionID);
        } else if (transactionType === 'PayPal') {
          await fetchPayPalWithdrawTransaction(TransactionID);
        } else if (transactionType === 'Venmo') {
          await fetchVenmoWithdrawTransaction(TransactionID);
        }
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred'));
      } finally {
        blockingLoader.hide();
      }
    };

    fetchTransaction();
  }, [TransactionID, transactionType]);

  return (
    <div>
      {transaction && (
        <DigitalWalletTransactionInfo
          transaction={transaction}
          transactionType={transactionType}
          fullName={fullName}
          jwt={jwt}
          ELinxRequestID={ELinxRequestID}
          {...props}
        />
      )}
    </div>
  );
}
